import { ModuleAndIdMapping, Modules } from "../constants/DatabaseConstants/MasterConstant";
import type FixMeLater from "../shared/types/fixMeLater.type";

class MasterUtil {
  public static commonFunction = async (data: FixMeLater[]) => {
    return data?.map((item: FixMeLater, index: number) => {
      return {
        ...item,
        sr_no: index + 1,
        key: item?.id
      };
    });
  };

  /* Master get Records List formatting */
  public static formattedResponse = async (modelId: number, data: FixMeLater) => {
    switch (modelId) {
      case ModuleAndIdMapping[Modules.USER_ROLE_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            role_name: item?.role?.rolename,
            user_name: item?.user?.username,
            user_full_name: `${item?.user?.last_name} ${item?.user?.first_name}`
          };
        });
      case ModuleAndIdMapping[Modules.CUSTOMER_LOCATION_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            customer_name: item?.customer?.customer_name,
            location_type_name: item?.locationType?.location_type_name,
            parking_map_type_name: item?.parkingMapType?.parking_map_type_name,
            projectId_deviceId: `${item?.project_id ?? ""} / ${item?.device_id ?? ""}`
          };
        });
      case ModuleAndIdMapping[Modules.CUSTOMER_CLIENT_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            customer_name: item?.customer?.customer_name,
            location_name: item?.customerLocation?.location_name
          };
        });
      case ModuleAndIdMapping[Modules.KEY_FOB_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            customer_name: item?.customer?.customer_name,
            location_name: item?.customerLocation?.location_name,
            vehicle_type_name: item?.vehicleType?.vehicle_type_name,
            dimension: item?.vehicleType?.dimension,
            parkingStatusName: item?.parkingStatus?.name
          };
        });
      case ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            customer_name: item?.customer?.customer_name,
            location_name: item?.customerLocation?.location_name,
            client_name: item?.customerClient?.client_name,
            key_fob_token: item?.keyFob?.key_fob_token,
            mobile_portal_user_name: `${item?.mobileUser?.first_name ?? ""} ${item?.mobileUser?.last_name ?? ""}`,
            first_name: item?.mobileUser?.first_name,
            last_name: item?.mobileUser?.last_name,
            employee_id: item?.employee_id,
            mobile_number: item?.mobileUser?.mobile_number,
            mobile_portal_user_id: item?.mobileUser?.id,
            parkingStatusName: item?.logParkingRequest?.length === 0 ? "" : item?.keyFob?.parkingStatus?.name
          };
        });

      case ModuleAndIdMapping[Modules.CONFIG_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            name: item?.key
          };
        });

      case ModuleAndIdMapping[Modules.WEB_FE_ACCESS_CONTROL_MASTER]:
        return data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            role_name: item?.role?.rolename,
            module_name: item?.module?.name ? MasterUtil.formatModuleName(item?.module?.name) : "",
            operation_names: item?.operations.map((op: { name: string }) => op.name.toUpperCase()).join(", ")
          };
        });
      case ModuleAndIdMapping[Modules.PRICING_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            customer_name: item?.customer?.customer_name,
            location_name: item?.customerLocation?.location_name,
            vehicle_type_name: item?.vehicleType?.vehicle_type_name
          };
        });
      case ModuleAndIdMapping[Modules.FAQ_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1
          };
        });

      case ModuleAndIdMapping[Modules.ADVANCE_BOOKING_QUOTA_MASTER]:
        return await data?.map((item: FixMeLater, index: number) => {
          return {
            ...item,
            sr_no: index + 1,
            vehicle_type_name: item?.vehicleType?.vehicle_type_name
          };
        });

      default:
        return (await MasterUtil.commonFunction(data)) || data || [];
    }
  };

  public static formatModuleName = (moduleName: string) => {
    // Replace hyphens with spaces
    let formattedName = moduleName.replace(/-/g, " ");

    // Capitalize each word's first letter
    formattedName = formattedName.replace(/\b\w/g, (char) => char.toUpperCase());

    return formattedName;
  };

  public static formattedMasterDataToExportFile = (data: any[], moduleId: number | null) => {
    switch (moduleId) {
      case ModuleAndIdMapping[Modules.KEY_FOB_MASTER]:
        return data?.map((item: FixMeLater, index: number) => {
          return {
            "Sr No.": index + 1,
            Customer: item?.customer?.customer_name,
            Location: item?.customerLocation?.location_name,
            "KeyFob Token": item?.key_fob_token,
            "Vehicle Type": item?.vehicleType?.vehicle_type_name ?? "",
            Dimension: item?.vehicleType?.dimension ?? "",
            "Parking Status": item?.parkingStatus?.name ?? ""
          };
        });
      case ModuleAndIdMapping[Modules.KEY_FOB_ALLOCATION_MASTER]:
        return data?.map((item: FixMeLater, index: number) => {
          return {
            "Sr No.": index + 1,
            Customer: item?.customer?.customer_name,
            Location: item?.customerLocation?.location_name,
            Client: item?.customerClient?.client_name ?? "",
            "KeyFob Details": `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name ?? ""} - ${item?.keyFob?.vehicleType?.dimension ?? ""} `,
            "Vehicle Number": item?.vehicle_number ?? "",
            "Vehicle Make": item?.vehicle_make ?? "",
            Username: `${item?.mobileUser?.first_name ?? ""} ${item?.mobileUser?.last_name ?? ""}`,
            "Mobile Number": item?.mobileUser?.mobile_number,
            "Parking Status": item?.logParkingRequest?.length === 0 ? "" : item?.keyFob?.parkingStatus?.name ?? ""
          };
        });
      default:
        return data;
    }
  };
}

export default MasterUtil;
